import {
  ActionIcon,
  Box,
  PasswordInput,
  PasswordInputProps,
  Progress,
  Text,
  Tooltip
} from '@mantine/core';
import { IconRefresh } from '@tabler/icons';
import { FC, SyntheticEvent, useRef } from 'react';
import { getPasswordStrength } from '@/utils/passwordUtils';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { useDisclosure } from '@mantine/hooks';
import { preserveCursorPosition } from '@/utils/preserveCursorPosition';

interface Rule {
  condition: boolean;
  label: string;
}

interface IPasswordInput extends PasswordInputProps {
  value: string;
  label: string;
  summary: string;
  rules: Rule[];
  placeholder?: string;
  onChange: (event: SyntheticEvent<HTMLInputElement>) => void;
  handleGeneratePassword: () => void;
  size?: string;
}

const getPasswordStrengthByRules = (password: string, rules: Rule[]): number => {
  if (!password) return 10;

  const passedCount = rules.filter((rule) => rule.condition).length;
  const totalCount = rules.length;

  // Минимальная сила — 10, максимальная — 100
  return Math.max(10, Math.round((passedCount / (totalCount || 1)) * 100));
};

export const PasswordInputStrengthMeter: FC<IPasswordInput> = ({
  value,
  label,
  summary,
  placeholder,
  onChange,
  rules,
  handleGeneratePassword,
  size = 'sm',
  error = ''
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const strength = getPasswordStrengthByRules(value, rules);
  const { color, strengthString } = getPasswordStrength(strength, t);
  const [visible, { toggle }] = useDisclosure(false);
  const passwordInputStrengthRef = useRef<HTMLInputElement | null>(null);

  const fixCursorLocation = (ref: React.RefObject<HTMLInputElement>) => {
    return () => {
      preserveCursorPosition(ref, toggle);
    };
  };

  return (
    <Box className={classes.container}>
      <Box>
        <PasswordInput
          size={size}
          radius="md"
          variant="filled"
          data-value="password"
          label={label}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          className={classes.input}
          withAsterisk={true}
          error={error}
          ref={passwordInputStrengthRef}
          visible={visible}
          onVisibilityChange={fixCursorLocation(passwordInputStrengthRef)}
        />
      </Box>

      <Box className={classes.field}>
        <Box className={classes.passwordProgress}>
          <Text className={classes.passwordStatus} fz="sm">{`${t(
            'passwordReset.passwordStrength'
          )}: ${strengthString}`}</Text>
          <Progress color={color} value={strength} size={5} className={classes.progress} />
        </Box>
        <Tooltip label={t('requestResetPassword.generateStrongPassword')}>
          <ActionIcon className={classes.icon} onClick={handleGeneratePassword}>
            <Box className={classes.generateButton}>
              <IconRefresh />
              {t('passwordReset.create')}
            </Box>
          </ActionIcon>
        </Tooltip>
      </Box>
      <Text fz="xs" className={classes.hint}>
        {summary}
      </Text>
    </Box>
  );
};
